import { formatDisplayDateTime } from "@/utils/date-format";
import { exportToExcel } from "@/utils/export-to-excel";
import { fetchAllListRows } from "@/utils/fetch-all-list-rows";
import type { DataProvider } from "react-admin";
import type { ExcelColumn } from "@/types/excel";
import {
  formatAttendanceChannel,
  formatAttendanceMatchSource,
  formatCheckedInBy,
  isSabilHolderRecord,
} from "./miqaat-attendance-display";

export function buildMiqaatAttendanceExportColumns(): ExcelColumn<Record<string, unknown>>[] {
  return [
    { header: "ITS ID", field: (r) => r.attendeeIts || "", width: 14 },
    { header: "Full Name", field: (r) => r.attendeeName || "", width: 26 },
    { header: "Phone", field: (r) => r.phone || "", width: 16 },
    {
      header: "Affiliation",
      field: (r) => (isSabilHolderRecord(r) ? "Sabil Holder" : "Mehmaan / Visitor"),
      width: 20,
    },
    {
      header: "RSVP Status",
      field: (r) => (r.rsvpOnFile ? "With RSVP" : "Without RSVP (Walk-in)"),
      width: 24,
    },
    {
      header: "Channel",
      field: (r) => formatAttendanceChannel(r.channel),
      width: 18,
    },
    {
      header: "Match Source",
      field: (r) => formatAttendanceMatchSource(r.matchSource),
      width: 14,
    },
    { header: "Jamaat / Mohalla", field: (r) => r.jamaatMohalla || "", width: 28 },
    {
      header: "Checked In By",
      field: (r) =>
        formatCheckedInBy(r as { checkedInByPhone?: unknown; checkedInByAttendeeKey?: unknown }),
      width: 20,
    },
    {
      header: "Check-in Time",
      field: (r) => (r.createdAt ? formatDisplayDateTime(String(r.createdAt)) : ""),
      width: 22,
    },
    { header: "Notes", field: (r) => r.notes || "", width: 30 },
  ];
}

export async function exportMiqaatAttendanceToExcel(
  dataProvider: DataProvider,
  options: {
    miqaatId?: string;
    miqaatName?: string;
    venueId?: string;
    filter?: Record<string, unknown>;
  }
) {
  const { miqaatId, miqaatName = "miqaat", venueId, filter = {} } = options;
  const listFilter: Record<string, unknown> = {
    ...filter,
    ...(miqaatId ? { miqaatId } : {}),
    ...(venueId ? { venueId } : {}),
  };

  const rows = await fetchAllListRows(dataProvider, "miqaatAttendance", {
    filter: listFilter,
    sort: { field: "createdAt", order: "ASC" },
    maxResults: 10000,
  });

  const sanitizedName =
    String(miqaatName)
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, "_") || "miqaat";
  const filenamePrefix = `attendance_${sanitizedName}`;

  const columns = buildMiqaatAttendanceExportColumns();
  return exportToExcel(columns, rows as Record<string, unknown>[], {
    filenamePrefix,
    sheetName: "Attendance",
  });
}
